import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { GetInvestigationSummaryRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getInvestigationSummaryRPC";
import { LeoRPCResult, LeoUUID } from "@surya-digital/leo-ts-types";
import { getAPIClient } from "../../networking/APIClient";
import { useGetInvestigationSummaryRPCClientImpl } from "../rpcs/RPC";

const getInitialState = (): SnapshotIn<
  typeof GetInvestigationSummaryStore
> => ({
  summary: EMPTY_CHARACTER,
  loadingInvestigationSummary: false,
  getInvestigationSummaryRPCError: null,
});

export const GetInvestigationSummaryStore = types
  .model("GetInvestigationSummaryStore", {
    summary: types.string,
    loadingInvestigationSummary: types.boolean,
    getInvestigationSummaryRPCError: types.maybeNull(
      types.enumeration(
        "GetInvestigationSummaryRPCError",
        Object.values(GetInvestigationSummaryRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    getInvestigationSummary: flow(function* (emailId: string) {
      store.loadingInvestigationSummary = true;
      store.getInvestigationSummaryRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetInvestigationSummaryRPC.Request(
          new LeoUUID(emailId),
        );

        const result: LeoRPCResult<
          GetInvestigationSummaryRPC.Response,
          GetInvestigationSummaryRPC.Errors.Errors
        > =
          yield useGetInvestigationSummaryRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          try {
            store.summary = JSON.stringify(
              JSON.parse(String.raw`${result.response.summary}`),
              null,
              2,
            );
          } catch {
            store.summary = result.response.summary;
          }
          return;
        }

        switch (result.error.code) {
          case GetInvestigationSummaryRPC.RPCError.InvalidEmailId:
          case GetInvestigationSummaryRPC.RPCError
            .InvestigationSummaryNotAvailable:
            store.getInvestigationSummaryRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingInvestigationSummary = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createGetInvestigationSummaryStore = (): Instance<
  typeof GetInvestigationSummaryStore
> => GetInvestigationSummaryStore.create(getInitialState());
